import axios from 'axios';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { getApiUrl } from '../config/development';
import { isWeb } from '../utils/platform';
import { API_BASE_URL } from '../utils/constants';

const SAFE_DEFAULT_URL = "https://yc.intuitlms.online/api/";
const API_URL_RAW = getApiUrl() || API_BASE_URL || SAFE_DEFAULT_URL;
export const FINAL_API_URL = API_URL_RAW.endsWith('/') ? API_URL_RAW : `${API_URL_RAW}/`;

console.log('[API Init] Target URL:', FINAL_API_URL);

let logoutHandler = null;
/**
 * Global setter to allow AuthContext to listen for 401 Unauthorized errors
 * and clear its React state accordingly.
 */
export const onUnauthorized = (handler) => {
  logoutHandler = handler;
};

// Create axios instance with custom configuration for mobile environment
const api = axios.create({
  baseURL: FINAL_API_URL,
  headers: {
    'Accept': 'application/json'
  },
  timeout: isWeb ? 30000 : 90000, // Further increased for slow uploads
  transformRequest: [(data, headers) => {
    // 1. Handle FormData (Files/Media)
    if (data && (typeof data.append === 'function')) {
      // For FormData, let the browser/native layer set the Content-Type with boundary
      if (headers) delete headers['Content-Type'];
      return data;
    }

    // 2. Handle Plain Objects (JSON)
    if (data && typeof data === 'object') {
      if (headers) headers['Content-Type'] = 'application/json';
      return JSON.stringify(data);
    }

    return data;
  }],
  // Increase payload limits for mobile uploads
  maxContentLength: Infinity,
  maxBodyLength: Infinity,
  validateStatus: (status) => status >= 200 && status < 300,
});

// Consolidated request interceptor for better reliability
api.interceptors.request.use(
  async (config) => {
    // 1. Ensure baseURL is set (fallback for any race conditions)
    if (!config.baseURL || config.baseURL === 'undefined') {
      config.baseURL = FINAL_API_URL;
    }

    // 2. Identify data type
    const isFormData = config.data && (typeof config.data.append === 'function');

    // 3. Handle Content-Type
    if (isFormData) {
      // CRITICAL: Force remove Content-Type for FormData
      // This is the #1 cause of "No response" / "Invalid boundary" on mobile
      delete config.headers['Content-Type'];
    } else if (config.data && !config.headers['Content-Type']) {
      config.headers['Content-Type'] = 'application/json';
    }

    // 4. Trace the request
    if (!isWeb) {
      console.log(`[API Mobile] ${config.method?.toUpperCase()} ${config.baseURL}${config.url}`,
        isFormData ? '(Multipart/FormData)' : '(JSON)');
    }

    // 5. Add Authorization header
    try {
      const isParamAdminRequest = config.url && (config.url.includes('/admin') || config.url.includes('admin/'));

      let token = null;
      if (isParamAdminRequest) {
        // Prioritize admin token for admin routes
        token = await AsyncStorage.getItem('adminAuthToken');
        if (!token) token = await AsyncStorage.getItem('token');
      } else {
        // Prioritize user token for other routes
        token = await AsyncStorage.getItem('token');
        if (!token) token = await AsyncStorage.getItem('adminAuthToken');
      }

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      console.error('Error getting auth token:', error);
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response) {
      const { status, data } = error.response;

      if (status === 401) {
        // Clear all auth data on 401
        try {
          await AsyncStorage.multiRemove(['token', 'adminAuthToken', 'user']);
          if (logoutHandler) logoutHandler();
        } catch (e) {
          console.error('Error clearing tokens:', e);
        }
      } else if (status === 403 && data?.status === 'suspended') {
        const message = data.error || 'Your account has been suspended. Please contact support.';

        // Alert user
        if (typeof alert !== 'undefined') {
          alert(message);
        }

        // Clear tokens
        try {
          await AsyncStorage.multiRemove(['token', 'adminAuthToken', 'user']);
          // For web/admin
          if (typeof localStorage !== 'undefined') {
            localStorage.clear();
          }
        } catch (e) {
          console.error('Error clearing tokens on suspension:', e);
        }
      }
    }

    console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// Utility to clean endpoint by removing duplicate /api prefix
const getCleanEndpoint = (endpoint) => {
  let clean = endpoint;
  if (endpoint.startsWith('/api/')) {
    clean = endpoint.substring(5);
  } else if (endpoint.startsWith('/api')) {
    clean = endpoint.substring(4);
  }

  // Ensure we return a relative path (no leading slash)
  // so it correctly appends to baseURL (e.g., .../api/ + social/posts)
  if (clean.startsWith('/')) {
    clean = clean.substring(1);
  }
  return clean;
};

export const getData = async (endpoint, params = {}, retries = 3) => {
  // Defensive: If params is a number, someone's using the old (endpoint, retries) signature
  let actualParams = params;
  let actualRetries = retries;
  if (typeof params === 'number') {
    actualParams = {};
    actualRetries = params;
  }

  let lastError = new Error(`Network failure calling ${endpoint}`);
  const cleanEndpoint = getCleanEndpoint(endpoint);

  for (let attempt = 1; attempt <= actualRetries; attempt++) {
    try {
      const response = await api.get(cleanEndpoint, { params: actualParams });
      return response.data || { success: false, error: 'No data returned' };
    } catch (error) {
      lastError = error;
      // Don't retry on client errors (4xx)
      if (error.response?.status >= 400 && error.response?.status < 500) {
        throw error;
      }
      if (attempt === actualRetries) throw error;
      await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
    }
  }
  throw lastError;
};

// Helper for robust mobile FormData uploads (Fetch fallback)
const performMobileUpload = async (method, endpoint, data) => {
  const cleanEndpoint = getCleanEndpoint(endpoint);
  console.log(`[API Mobile] Using Fetch fallback (Native) for ${method} ${cleanEndpoint}`);

  const isParamAdminRequest = endpoint && (endpoint.includes('/admin') || endpoint.includes('admin/'));

  let token = null;
  if (isParamAdminRequest) {
    token = await AsyncStorage.getItem('adminAuthToken');
    if (!token) token = await AsyncStorage.getItem('token');
  } else {
    token = await AsyncStorage.getItem('token');
    if (!token) token = await AsyncStorage.getItem('adminAuthToken');
  }

  const fullUrl = `${FINAL_API_URL}${cleanEndpoint}`;

  const response = await fetch(fullUrl, {
    method: method,
    headers: {
      'Accept': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      // IMPORTANT: No Content-Type set here
    },
    body: data,
  });

  const responseText = await response.text();
  let responseData;
  try {
    responseData = JSON.parse(responseText);
  } catch (e) {
    responseData = { error: 'Invalid server response', text: responseText };
  }

  if (!response.ok) {
    const error = new Error(responseData.error || `${method} failed`);
    error.response = { status: response.status, data: responseData };
    throw error;
  }
  return responseData;
};

export const postData = async (endpoint, data) => {
  try {
    const isFormData = data && (typeof data.append === 'function');
    if (isFormData && !isWeb) {
      return await performMobileUpload('POST', endpoint, data);
    }

    const cleanEndpoint = getCleanEndpoint(endpoint);
    const response = await api.post(cleanEndpoint, data);
    return response.data;
  } catch (error) {
    const errorData = error.response?.data;
    const status = error.response?.status;
    console.error(`Post Error ${endpoint} [${status || 'Network'}]:`, errorData || error.message);
    throw error;
  }
};

export const putData = async (endpoint, data) => {
  try {
    const isFormData = data && (typeof data.append === 'function');
    if (isFormData && !isWeb) {
      return await performMobileUpload('PUT', endpoint, data);
    }

    const cleanEndpoint = getCleanEndpoint(endpoint);
    const response = await api.put(cleanEndpoint, data);
    return response.data;
  } catch (error) {
    const errorData = error.response?.data;
    const status = error.response?.status;
    console.error(`Put Error ${endpoint} [${status || 'Network'}]:`, errorData || error.message);
    throw error;
  }
};

export const deleteData = async (endpoint) => {
  try {
    const cleanEndpoint = getCleanEndpoint(endpoint);
    const response = await api.delete(cleanEndpoint);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export default api;
